import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Settings, Plus, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, MenuItem, AddOnCategory, MenuItemAddOns } from '../../services/dataService';

interface MenuItemAddOnsConfigProps {
  menuItem: MenuItem;
  onClose: () => void;
  onSuccess: () => void;
}

const MenuItemAddOnsConfig: React.FC<MenuItemAddOnsConfigProps> = ({ 
  menuItem, 
  onClose, 
  onSuccess 
}) => {
  const { tenantData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<AddOnCategory[]>([]);
  const [currentConfig, setCurrentConfig] = useState<MenuItemAddOns | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    if (!tenantData?.id) return;
    
    try {
      setLoading(true);
      const [categoriesData, configData] = await Promise.all([
        dataService.getAddOnCategories(tenantData.id),
        dataService.getMenuItemAddOns(tenantData.id, menuItem.id!)
      ]);
      setCategories(categoriesData);
      setCurrentConfig(configData);
    } catch (error) {
      console.error('Error loading add-on data:', error);
      setError('Failed to load add-on data');
    } finally {
      setLoading(false);
    }
  }, [tenantData, menuItem.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleCategoryExpansion = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const addCategoryToMenuItem = (category: AddOnCategory) => {
    if (!currentConfig) {
      // Create new configuration
      const newConfig: MenuItemAddOns = {
        menuItemId: menuItem.id!,
        addOnCategories: [{
          categoryId: category.id!,
          categoryName: category.name,
          isRequired: category.isRequired,
          minSelections: category.minSelections,
          maxSelections: category.maxSelections,
          displayOrder: category.displayOrder
        }]
      };
      setCurrentConfig(newConfig);
    } else {
      // Add to existing configuration
      const newConfig = {
        ...currentConfig,
        addOnCategories: [
          ...currentConfig.addOnCategories,
          {
            categoryId: category.id!,
            categoryName: category.name,
            isRequired: category.isRequired,
            minSelections: category.minSelections,
            maxSelections: category.maxSelections,
            displayOrder: category.displayOrder
          }
        ]
      };
      setCurrentConfig(newConfig);
    }
  };

  const removeCategoryFromMenuItem = (categoryId: string) => {
    if (!currentConfig) return;
    
    const newConfig = {
      ...currentConfig,
      addOnCategories: currentConfig.addOnCategories.filter(cat => cat.categoryId !== categoryId)
    };
    setCurrentConfig(newConfig);
  };

  const updateCategorySettings = (categoryId: string, updates: Partial<{
    isRequired: boolean;
    minSelections: number;
    maxSelections: number;
    displayOrder: number;
  }>) => {
    if (!currentConfig) return;
    
    const newConfig = {
      ...currentConfig,
      addOnCategories: currentConfig.addOnCategories.map(cat => 
        cat.categoryId === categoryId ? { ...cat, ...updates } : cat
      )
    };
    setCurrentConfig(newConfig);
  };

  const moveCategory = (categoryId: string, direction: 'up' | 'down') => {
    if (!currentConfig) return;
    
    const currentIndex = currentConfig.addOnCategories.findIndex(cat => cat.categoryId === categoryId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= currentConfig.addOnCategories.length) return;
    
    const newCategories = [...currentConfig.addOnCategories];
    const [movedCategory] = newCategories.splice(currentIndex, 1);
    newCategories.splice(newIndex, 0, movedCategory);
    
    // Update display order for all affected categories
    const updatedCategories = newCategories.map((cat, index) => ({
      ...cat,
      displayOrder: index
    }));
    
    setCurrentConfig({
      ...currentConfig,
      addOnCategories: updatedCategories
    });
  };

  const handleSave = async () => {
    if (!tenantData?.id || !currentConfig) return;
    
    try {
      setLoading(true);
      setError('');
      
      await dataService.configureMenuItemAddOns(
        tenantData.id, 
        menuItem.id!, 
        currentConfig.addOnCategories
      );
      
      onSuccess();
    } catch (error) {
      console.error('Error saving menu item add-ons configuration:', error);
      setError('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableCategories = () => {
    if (!currentConfig) return categories;
    const usedCategoryIds = currentConfig.addOnCategories.map(cat => cat.categoryId);
    return categories.filter(cat => !usedCategoryIds.includes(cat.id!));
  };

  // Lock body scroll while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Configure Add-Ons for {menuItem.name}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select which add-on categories customers can choose from when ordering this item
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Current Configuration */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              Current Add-On Categories
            </h3>
            
            {(!currentConfig || currentConfig.addOnCategories.length === 0) ? (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <Settings className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">
                  No add-on categories configured yet. Add some categories below to get started.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {currentConfig.addOnCategories
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((category, index) => (
                    <div key={category.categoryId} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => moveCategory(category.categoryId, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Move up"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => moveCategory(category.categoryId, 'down')}
                              disabled={index === currentConfig.addOnCategories.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Move down"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">{category.categoryName}</h4>
                            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                category.isRequired 
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' 
                                  : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                              }`}>
                                {category.isRequired ? 'Required' : 'Optional'}
                              </span>
                              <span>Min: {category.minSelections}</span>
                              <span>Max: {category.maxSelections === 0 ? 'Unlimited' : category.maxSelections}</span>
                              <span>Order: {category.displayOrder + 1}</span>
                            </div>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => removeCategoryFromMenuItem(category.categoryId)}
                          className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Remove category"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Category Settings */}
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Required
                          </label>
                          <input
                            type="checkbox"
                            checked={category.isRequired}
                            onChange={(e) => updateCategorySettings(category.categoryId, { isRequired: e.target.checked })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Min Selections
                          </label>
                          <input
                            type="number"
                            value={category.minSelections}
                            onChange={(e) => updateCategorySettings(category.categoryId, { minSelections: parseInt(e.target.value) || 0 })}
                            min="0"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Max Selections
                          </label>
                          <input
                            type="number"
                            value={category.maxSelections}
                            onChange={(e) => updateCategorySettings(category.categoryId, { maxSelections: parseInt(e.target.value) || 0 })}
                            min="0"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Display Order
                          </label>
                          <input
                            type="number"
                            value={category.displayOrder}
                            onChange={(e) => updateCategorySettings(category.categoryId, { displayOrder: parseInt(e.target.value) || 0 })}
                            min="0"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Available Categories to Add */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              Available Categories to Add
            </h3>
            
            {getAvailableCategories().length === 0 ? (
              <div className="text-center py-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-gray-500 dark:text-gray-400">
                  All available add-on categories have been added to this menu item.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {getAvailableCategories().map(category => (
                  <div key={category.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">{category.name}</h4>
                        {category.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{category.description}</p>
                        )}
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mt-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            category.isRequired 
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' 
                              : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                          }`}>
                            {category.isRequired ? 'Required' : 'Optional'}
                          </span>
                          <span>Min: {category.minSelections}</span>
                          <span>Max: {category.maxSelections === 0 ? 'Unlimited' : category.maxSelections}</span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => addCategoryToMenuItem(category)}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !currentConfig}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <span>Save Configuration</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MenuItemAddOnsConfig;

